import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { n } from "../lib/format";
import { Cover } from "./Illustration";
import { LikeBar } from "./Bits";
import { prefetchProps } from "../lib/prefetch";

export const TopicDot = ({ color, size = 10 }) => (
  <span aria-hidden="true" style={{ display: "inline-block", flex: "none", width: size, height: size, borderRadius: "50%", background: color || "#64748b", boxShadow: "0 0 0 2px #fff" }} />
);

const textOn = (hex) => {
  const v = parseInt(hex.slice(1), 16);
  const lum = ((v >> 16) * 299 + ((v >> 8) & 255) * 587 + (v & 255) * 114) / 1000;
  return lum > 160 ? "#14232a" : "#fff";
};

/** Coloured topic tags over a card's picture; each links to the map filtered by that topic. */
export const TopicBadge = ({ p, style, large = false, compact = false, testId }) => {
  const [first, second, ...rest] = p.topic_items || [];
  if (!first) return null;
  const badge = (t, id, small = false, label = t.name) => (
    <Link to={`/?topic=${t.id}`} className={`topic-badge${small ? " topic-badge-sm" : ""}`} title={`See all ${t.name} proposals on the map`} data-testid={id}
      style={{ background: t.color, color: textOn(t.color) }}>{label}</Link>
  );
  const dot = (t, id) => (
    <Link key={t.id} to={`/?topic=${t.id}`} className="topic-badge topic-badge-dot" title={`See all ${t.name} proposals on the map`} aria-label={t.name} data-testid={id}
      style={{ background: t.color }} />
  );
  return (
    <div className={`topic-badges${large ? " lg" : ""}`} style={style} data-testid={`${testId}-group`}>
      {badge(first, testId)}
      {compact
        ? [second, ...rest].filter(Boolean).map((t, i) => dot(t, `${testId}-${i + 2}`))
        : <>{second && badge(second, `${testId}-2`, true)}{rest.length > 0 && badge(rest[0], `${testId}-more`, true, `+${rest.length}`)}</>}
    </div>
  );
};

const Flags = ({ p }) => (
  <>
    {p.trending && <span className="flag flag-trending">Trending</span>}
    {p.is_new && !p.trending && <span className="flag flag-new">New</span>}
  </>
);

export const ListCard = ({ p, selected, onShow }) => (
  <article className="list-card card-stretch" data-testid={`list-card-${p.id}`}
    style={{ display: "flex", gap: 14, padding: 12, borderRadius: 14, border: `1.5px solid ${selected ? "#C8102E" : "#e3e9eb"}`, background: selected ? "#fdf3f4" : "#fff" }}>
    <div style={{ position: "relative", flex: "none", width: 148, height: 112, borderRadius: 10, overflow: "hidden" }}>
      <Cover p={p} />
      <TopicBadge p={p} compact testId={`list-card-topic-${p.id}`} />
    </div>
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
      <p style={{ margin: 0, fontSize: 13, color: "var(--muted)", display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        <span>{p.place}</span><Flags p={p} />
      </p>
      <h3 className="head" style={{ margin: 0, fontSize: 19, lineHeight: 1.2, fontWeight: 700 }}>
        <Link className="card-link" to={`/proposal/${p.slug}`} {...prefetchProps(p.slug)} data-testid={`list-card-link-${p.id}`}>{p.title}</Link>
      </h3>
      <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, maxWidth: 120 }}><LikeBar pct={p.pct} /></div>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{n(p.likes)} <span style={{ fontWeight: 400, color: "var(--muted)" }}>likes · {p.pct}% positive</span></span>
      </div>
      {p.week_likes > 0 && <p className="meta">+{n(p.week_likes)} likes this week</p>}
    </div>
    <button type="button" className="icon-btn card-raise" aria-label={`Show ${p.title} on the map`} onClick={() => onShow(p)}
      style={{ alignSelf: "flex-start", color: "#C8102E" }} data-testid={`list-card-show-${p.id}`}>
      <MapPin size={18} />
    </button>
  </article>
);

export const Tile = ({ p, rank, by = false, children, testId }) => (
  <li className="hover-lift card-stretch" data-testid={testId || `tile-${p.id}`}
    style={{ position: "relative", background: "#fff", border: "1px solid #dbe4e7", borderRadius: 18, overflow: "hidden", display: "flex", flexDirection: "column", listStyle: "none" }}>
    <div style={{ position: "relative", aspectRatio: "16 / 10", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0 }}><Cover p={p} /></div>
      {rank && (
        <span aria-hidden="true" className="head" style={{ position: "absolute", top: 14, left: 14, width: 44, height: 44, borderRadius: 12, background: "#14232a",
          color: "#fff", fontSize: 24, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{rank}</span>
      )}
      {!rank && p.trending && <span className="flag flag-trending" style={{ position: "absolute", top: 12, left: 12, fontSize: 12, padding: "5px 10px" }}>Trending</span>}
      <TopicBadge p={p} large style={{ left: 14, bottom: 14 }} testId={`tile-topic-${p.id}`} />
    </div>
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
      <p className="meta">{by ? `${p.place} · by ${p.author_name}` : p.place}</p>
      <h3 className="head" style={{ margin: 0, fontSize: 22, lineHeight: 1.2, fontWeight: 700 }}>
        <Link className="card-link" to={`/proposal/${p.slug}`} {...prefetchProps(p.slug)} data-testid={`tile-link-${p.id}`}>
          {rank && <span className="sr-only">Number {rank}: </span>}{p.title}
        </Link>
      </h3>
      <div style={{ marginTop: "auto" }}>
        <LikeBar pct={p.pct} height={8} grey={by} />
        <p style={{ margin: "8px 0 0", fontSize: 14 }}><strong>{n(p.likes)}</strong> likes <span style={{ color: "var(--muted)" }}>· {n(p.dislikes)} dislikes</span></p>
      </div>
    </div>
    {children && <div className="card-raise">{children}</div>}
  </li>
);
